/**
 * MCP-Server Einstellungen - Claude Integration
 * Zeigt Konfiguration und Status des MCP-Servers
 */

import { useState, useEffect } from 'react';
import { Bot, Copy, Check, RefreshCw, ExternalLink, Eye, EyeOff, Download, Terminal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function MCPSettings() {
  const [status, setStatus] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);

  // Status und Manifest laden
  useEffect(() => {
    const load = async () => {
      try {
        const [statusRes, manifestRes] = await Promise.all([
          fetch(`${API_URL}/api/mcp/health`),
          fetch(`${API_URL}/api/mcp/manifest`)
        ]);
        
        if (statusRes.ok) setStatus(await statusRes.json());
        if (manifestRes.ok) setManifest(await manifestRes.json());
      } catch (error) {
        console.error('MCP Status laden Fehler:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Config kopieren
  const copyConfig = () => {
    const config = {
      mcpServers: {
        "olympus-betreuung": {
          command: "npx",
          args: [
            "mcp-remote",
            `${API_URL}/api/mcp`,
            "--header",
            "Authorization: Bearer olympus-mcp-2024"
          ]
        }
      }
    };
    
    navigator.clipboard.writeText(JSON.stringify(config, null, 2));
    setCopied(true);
    toast.success('Konfiguration in Zwischenablage kopiert');
    setTimeout(() => setCopied(false), 2000);
  };

  // Config herunterladen
  const downloadConfig = () => {
    const config = {
      mcpServers: {
        "olympus-betreuung": {
          command: "npx",
          args: [
            "mcp-remote",
            `${API_URL}/api/mcp`,
            "--header",
            "Authorization: Bearer olympus-mcp-2024"
          ]
        }
      }
    };
    
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'claude_desktop_config.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mcp-settings">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
          <Bot className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">MCP-Server (Claude Integration)</h2>
          <p className="text-sm text-slate-500">Model Context Protocol für KI-Assistenten</p>
        </div>
        <div className="ml-auto">
          {status?.status === 'online' ? (
            <Badge className="bg-green-100 text-green-700">Online</Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700">Offline</Badge>
          )}
        </div>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Terminal className="w-4 h-4" />
            Server-Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-slate-500">Status</p>
              <p className="font-medium text-slate-800">{status?.status || 'Unbekannt'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Version</p>
              <p className="font-medium text-slate-800">{status?.version || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Tools</p>
              <p className="font-medium text-slate-800">{manifest?.tools?.length || 0} verfügbar</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Endpunkt</p>
              <p className="font-medium text-slate-800 text-xs truncate">{API_URL}/api/mcp</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Token */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Authentifizierung</CardTitle>
          <CardDescription>Bearer-Token für MCP-Zugriff</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                value={showToken ? 'olympus-mcp-2024' : '••••••••••••••••'}
                readOnly
                className="pr-10 font-mono text-sm"
              />
              <button
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText('olympus-mcp-2024');
                toast.success('Token kopiert');
              }}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Token wird in <code className="bg-slate-100 px-1 rounded">backend/.env</code> als <code className="bg-slate-100 px-1 rounded">MCP_SECRET_TOKEN</code> definiert
          </p>
        </CardContent>
      </Card>

      {/* Claude Desktop Config */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Claude Desktop Konfiguration</CardTitle>
          <CardDescription>Fügen Sie diese Konfiguration zu Claude Desktop hinzu</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs text-slate-300 overflow-x-auto">
            <pre>{`{
  "mcpServers": {
    "olympus-betreuung": {
      "command": "npx",
      "args": [
        "mcp-remote",
        "${API_URL}/api/mcp",
        "--header",
        "Authorization: Bearer olympus-mcp-2024"
      ]
    }
  }
}`}</pre>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={copyConfig}>
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Kopiert!' : 'Kopieren'}
            </Button>
            <Button size="sm" variant="outline" onClick={downloadConfig}>
              <Download className="w-4 h-4 mr-2" />
              Als Datei
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href="https://claude.ai/download" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Claude Desktop
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Verfügbare Tools */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Verfügbare Tools ({manifest?.tools?.length || 0})</CardTitle>
          <CardDescription>Diese Tools kann Claude über MCP nutzen</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {manifest?.tools?.map((tool) => (
              <div key={tool.name} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <code className="text-xs font-semibold text-violet-700 bg-violet-100 px-2 py-0.5 rounded">
                    {tool.name}
                  </code>
                </div>
                <p className="text-xs text-slate-600">{tool.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Anleitung */}
      <Alert className="bg-blue-50 border-blue-200">
        <Bot className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Einrichtung:</strong> Öffnen Sie Claude Desktop → Settings → Developer → Edit Config → 
          Fügen Sie die obige Konfiguration ein → Speichern → Claude neu starten
        </AlertDescription>
      </Alert>
    </div>
  );
}
